import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { performanceApi } from '@/api/performance'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, Trophy, Medal } from 'lucide-react'
import { DateRangePicker } from '@/components/DateRangePicker'

const formatMoney = (v: number) => `¥${(v ?? 0).toFixed(2)}`

export function PerformanceRankingPage() {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const defaultRange = {
    start_date: firstDay.toISOString().slice(0, 10),
    end_date: today.toISOString().slice(0, 10),
  }
  const [dateRange, setDateRange] = useState(defaultRange)
  const [queryDateRange, setQueryDateRange] = useState(defaultRange)
  const [dimension, setDimension] = useState('employee')

  const { data: empData, isLoading: empLoading } = useQuery({
    queryKey: ['ranking-employee', queryDateRange.start_date, queryDateRange.end_date],
    queryFn: () => performanceApi.getRankings({
      start_date: queryDateRange.start_date,
      end_date: queryDateRange.end_date,
      dimension: 'employee',
    }),
    enabled: dimension === 'employee',
  })

  const { data: deptData, isLoading: deptLoading } = useQuery({
    queryKey: ['ranking-department', queryDateRange.start_date, queryDateRange.end_date],
    queryFn: () => performanceApi.getRankings({
      start_date: queryDateRange.start_date,
      end_date: queryDateRange.end_date,
      dimension: 'department',
    }),
    enabled: dimension === 'department',
  })

  const handleSearch = () => {
    setQueryDateRange(dateRange)
  }

  const renderMedal = (index: number) => {
    if (index === 0) return <Medal className="h-4 w-4 text-yellow-500" />
    if (index === 1) return <Medal className="h-4 w-4 text-gray-400" />
    if (index === 2) return <Medal className="h-4 w-4 text-orange-600" />
    return <span className="text-muted-foreground">{index + 1}</span>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Trophy className="h-8 w-8" />
          业绩排行
        </h1>
        <p className="text-muted-foreground mt-1">员工和单位业绩排行榜</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="space-y-1 w-56">
              <Label className="text-sm">日期范围</Label>
              <DateRangePicker
                value={dateRange}
                onChange={(v) => setDateRange({ start_date: v.start_date ?? '', end_date: v.end_date ?? '' })}
                className="w-full h-9"
              />
            </div>
            <Button onClick={handleSearch}>查询</Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={dimension} onValueChange={setDimension}>
        <TabsList>
          <TabsTrigger value="employee">员工排行</TabsTrigger>
          <TabsTrigger value="department">单位排行</TabsTrigger>
        </TabsList>

        <TabsContent value="employee">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">排名</TableHead>
                    <TableHead>员工</TableHead>
                    <TableHead className="text-right">总业绩</TableHead>
                    <TableHead className="text-right">订单数</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(empData ?? []).map((row: any, index: number) => (
                    <TableRow key={row.user_id ?? index}>
                      <TableCell>{renderMedal(index)}</TableCell>
                      <TableCell className="font-medium">{row.user_name}</TableCell>
                      <TableCell className="text-right font-bold">{formatMoney(row.amount)}</TableCell>
                      <TableCell className="text-right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {empLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {!empLoading && (empData ?? []).length === 0 && (
                <p className="text-center text-muted-foreground py-8">暂无数据</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="department">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">排名</TableHead>
                    <TableHead>单位</TableHead>
                    <TableHead className="text-right">总业绩</TableHead>
                    <TableHead className="text-right">出业绩人数</TableHead>
                    <TableHead className="text-right">订单数</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(deptData ?? []).map((row: any, index: number) => (
                    <TableRow key={row.dept_id ?? index}>
                      <TableCell>{renderMedal(index)}</TableCell>
                      <TableCell className="font-medium">{row.dept_name}</TableCell>
                      <TableCell className="text-right font-bold">{formatMoney(row.total_promotion)}</TableCell>
                      <TableCell className="text-right">{row.user_count}</TableCell>
                      <TableCell className="text-right">{row.total_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {deptLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {!deptLoading && (deptData ?? []).length === 0 && (
                <p className="text-center text-muted-foreground py-8">暂无数据</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
